import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast, { Toaster } from 'react-hot-toast';
import {
  Shield,
  Globe,
  Network,
  Server,
  Smartphone,
  Cloud,
  Target,
  FileText,
  User,
  Mail,
  Calendar,
  CheckCircle,
  AlertCircle,
  Loader2,
  Lock,
  Cpu,
  Clock,
  Phone,
  MapPin,
  XCircle
} from 'lucide-react';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 2rem 0;
`;

const ContentWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const IconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: ${props => props.gradient || 'linear-gradient(135deg, #3b82f6, #1d4ed8)'};
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const MainTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 0.75rem;
  
  @media (max-width: 1024px) {
    font-size: 2.5rem;
  }
`;

const SubTitle = styled.h2`
  font-size: 1.25rem;
  color: #475569;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  color: #64748b;
  font-size: 1rem;
  max-width: 768px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid #e2e8f0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.gradient || 'linear-gradient(135deg, #3b82f6, #1d4ed8)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: ${props => props.columns || '1fr 1fr'};
    gap: 1.5rem;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  
  ${props => props.fullWidth && `
    @media (min-width: 1024px) {
      grid-column: 1 / -1;
    }
  `}
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  
  svg {
    width: 16px;
    height: 16px;
    margin-right: 0.5rem;
    color: #6b7280;
  }
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: white;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: white;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: white;
  cursor: pointer;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.5rem;
  margin-bottom: 0;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  
  input {
    width: 16px;
    height: 16px;
    margin-right: 0.5rem;
    accent-color: #3b82f6;
  }
  
  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }
`;

const ExclusionsSection = styled.div`
  margin-top: 2rem;
`;

const ExclusionsTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const SubmitSection = styled.div`
  padding: 2rem;
  background: #f8fafc;
  text-align: center;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 2rem;
  background: ${props => props.gradient || 'linear-gradient(135deg, #3b82f6, #1d4ed8)'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  min-width: 256px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  
  svg {
    width: 64px;
    height: 64px;
    color: #ef4444;
    margin: 0 auto 1rem;
    display: block;
  }
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #64748b;
  }
`;

const scanTypeMapping = {
  'web-app': 'web',
  'network-infra': 'network',
  'firewall': 'firewall',
  'api-security': 'api',
  'thick-client': 'thick-client',
  'thin-client': 'thin-client',
  'mobile-app': 'mobile-pt',
  'red-teaming': 'red-team',
  'cloud-pentest': 'cloud-pt'
};

// Scan type configurations
const scanTypeConfig = {
  'web-app': {
    title: 'Web Application Security Assessment',
    icon: Globe,
    gradient: 'linear-gradient(135deg, #3b82f6, #1e40af)',
    color: 'blue'
  },
  'network-infra': {
    title: 'Network Infrastructure Penetration Test',
    icon: Network,
    gradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    color: 'purple'
  },
  'api-security': {
    title: 'API Security Assessment',
    icon: Server,
    gradient: 'linear-gradient(135deg, #10b981, #047857)',
    color: 'emerald'
  },
  'firewall': {
    title: 'Firewall Security Assessment',
    icon: Shield,
    gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
    color: 'red'
  },
  'thick-client': {
    title: 'Thick Client Security Assessment',
    icon: FileText,
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    color: 'amber'
  },
  'thin-client': {
    title: 'Thin Client Security Assessment',
    icon: Globe,
    gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)',
    color: 'cyan'
  },
  'mobile-app': {
    title: 'Mobile Application Security Assessment',
    icon: Smartphone,
    gradient: 'linear-gradient(135deg, #ec4899, #be185d)',
    color: 'pink'
  },
  'red-teaming': {
    title: 'Red Team Exercise',
    icon: Target,
    gradient: 'linear-gradient(135deg, #dc2626, #991b1b)',
    color: 'red'
  },
  'cloud-pentest': {
    title: 'Cloud Infrastructure Penetration Test',
    icon: Cloud,
    gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    color: 'sky'
  }
};

// Common timezones
const timezones = [
  { value: 'UTC', label: 'UTC (GMT+00:00)' },
  { value: 'America/New_York', label: 'Eastern Time (GMT-05:00)' },
  { value: 'America/Chicago', label: 'Central Time (GMT-06:00)' },
  { value: 'America/Denver', label: 'Mountain Time (GMT-07:00)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (GMT-08:00)' },
  { value: 'Europe/London', label: 'London (GMT+00:00)' },
  { value: 'Europe/Berlin', label: 'Berlin (GMT+01:00)' },
  { value: 'Europe/Moscow', label: 'Moscow (GMT+03:00)' },
  { value: 'Asia/Dubai', label: 'Dubai (GMT+04:00)' },
  { value: 'Asia/Kolkata', label: 'India (GMT+05:30)' },
  { value: 'Asia/Shanghai', label: 'China (GMT+08:00)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (GMT+09:00)' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+11:00)' }
];

export default function PreEngagementForm() {
  const { scanType } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    target_details: { urls: [] },
    objectives: '',
    scope: '',
    timeline: '',
    proposed_start_date: '',
    proposed_end_date: '',
    timezone: 'UTC',
    has_exclusions: false,
    exclusions: '',
    contact_info: { 
      name: '', 
      email: '', 
      phone: '',
      preferred_contact_time: ''
    }
  });
  const [submitting, setSubmitting] = useState(false);

  const config = scanType ? scanTypeConfig[scanType] : null;
  const IconComponent = config ? config.icon : Shield;

  // Helper: convert comma-separated string to array
  const toArray = (str) => 
    str.split(',').map(s => s.trim()).filter(Boolean);

  // Generalized change handler for direct keys or nested objects
  const handleChange = (section, key) => (e) => {
    const val = e.target.value;

    if (section === 'contact_info' || section === 'target_details') {
      setForm(prev => ({
        ...prev,
        [section]: { ...prev[section], [key]: val }
      }));
    } else {
      setForm(prev => ({
        ...prev,
        [key || section]: val
      }));
    }
  };

  const handleCheckboxChange = (field) => (e) => {
    setForm(prev => ({
      ...prev,
      [field]: e.target.checked
    }));
  };

  // Render fields for each scan type
  const renderTargetFields = () => {
    const td = form.target_details;

    switch (scanType) {
      case 'web-app':
        return (
          <FieldGrid>
            <Field fullWidth>
              <Label>
                <Globe />
                Application URL *
              </Label>
              <Input
                type="url"
                value={form.target_details.urls?.[0] || ''}
                onChange={(e) => {
                  setForm(prev => ({
                    ...prev,
                    target_details: {
                      ...prev.target_details,
                      urls: [e.target.value]
                    }
                  }));
                }}
                placeholder="https://example.com"
                required
              />
            </Field>
            <Field>
              <Label>
                <Cpu />
                Backend Technology
              </Label>
              <Input
                type="text"
                value={td.backend || ''}
                onChange={handleChange('target_details', 'backend')}
                placeholder="Django, Node.js, Ruby on Rails, etc."
              />
            </Field>
            <Field fullWidth>
              <Label>
                <Lock />
                Test Credentials
              </Label>
              <TextArea
                rows={4}
                value={td.credentials || ''}
                onChange={handleChange('target_details', 'credentials')}
                placeholder="username: testuser&#10;password: testpass123&#10;&#10;Or provide test accounts with different privilege levels..."
              />
              <HelpText>Provide test credentials or indicate if they need to be created</HelpText>
            </Field>
          </FieldGrid>
        );

      case 'network-infra':
      case 'firewall':
        return (
          <FieldGrid>
            <Field>
              <Label>
                <Network />
                IP Addresses & Ranges *
              </Label>
              <Input
                value={td.ips || ''}
                onChange={handleChange('target_details', 'ips')}
                placeholder="192.168.1.1, 10.0.0.0/24"
                required
              />
              <HelpText>Enter comma-separated IP addresses or CIDR ranges</HelpText>
            </Field>
          </FieldGrid>
        );

      case 'api-security':
        return (
          <FieldGrid>
            <Field>
              <Label>
                <Server />
                API Base URL *
              </Label>
              <Input
                type="url"
                value={td.baseURL || ''}
                onChange={handleChange('target_details', 'baseURL')}
                placeholder="https://api.example.com"
                required
              />
            </Field>
            <Field>
              <Label>
                <FileText />
                API Specification (OpenAPI/Swagger JSON)
              </Label>
              <TextArea
                rows={8}
                value={td.apiSpec || ''}
                onChange={handleChange('target_details', 'apiSpec')}
                placeholder="Paste your OpenAPI/Swagger specification here..."
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
            </Field>
          </FieldGrid>
        );

      case 'thick-client':
        return (
          <FieldGrid>
            <Field fullWidth>
              <Label>
                <FileText />
                Application Files URLs *
              </Label>
              <TextArea
                rows={3}
                value={td.application_files || ''}
                onChange={handleChange('target_details', 'application_files')}
                placeholder="https://example.com/app.exe, https://example.com/setup.msi"
                required
              />
            </Field>
            <Field>
              <Label>Application Name</Label>
              <Input
                value={td.application_info?.name || ''}
                onChange={e => {
                  const name = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    target_details: {
                      ...prev.target_details,
                      application_info: {
                        ...prev.target_details.application_info,
                        name
                      }
                    }
                  }));
                }}
                placeholder="MyApp"
              />
            </Field>
            <Field>
              <Label>Application Version</Label>
              <Input
                value={td.application_info?.version || ''}
                onChange={e => {
                  const version = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    target_details: {
                      ...prev.target_details,
                      application_info: {
                        ...prev.target_details.application_info,
                        version
                      }
                    }
                  }));
                }}
                placeholder="1.0.0"
              />
            </Field>
          </FieldGrid>
        );

      case 'thin-client':
        return (
          <FieldGrid>
            <Field>
              <Label>
                <Globe />
                Application URL *
              </Label>
              <Input
                type="url"
                value={form.target_details.urls?.[0] || ''}
                onChange={(e) => {
                  setForm(prev => ({
                    ...prev,
                    target_details: {
                      ...prev.target_details,
                      urls: [e.target.value]
                    }
                  }));
                }}
                placeholder="https://example-thin-client.com"
                required
              />
            </Field>
          </FieldGrid>
        );

      case 'mobile-app':
        return (
          <FieldGrid>
            <Field>
              <Label>
                <Smartphone />
                Platform *
              </Label>
              <Select
                value={td.platform || ''}
                onChange={handleChange('target_details', 'platform')}
                required
              >
                <option value="">Select Platform</option>
                <option value="android">Android</option>
                <option value="ios">iOS</option>
              </Select>
            </Field>
            <Field>
              <Label>APK/IPA File URL</Label>
              <Input
                type="url"
                value={td.app_file_url || ''}
                onChange={handleChange('target_details', 'app_file_url')}
                placeholder="https://example.com/app.apk"
              />
            </Field>
            <Field fullWidth>
              <Label>App Store URL</Label>
              <Input
                type="url"
                value={td.app_store_url || ''}
                onChange={handleChange('target_details', 'app_store_url')}
                placeholder="https://play.google.com/store/apps/details?id=..."
              />
            </Field>
          </FieldGrid>
        );

      case 'red-teaming':
        return (
          <FieldGrid>
            <Field>
              <Label>
                <Target />
                IP Ranges
              </Label>
              <Input
                value={td.ip_ranges || ''}
                onChange={handleChange('target_details', 'ip_ranges')}
                placeholder="192.168.1.0/24, 10.0.0.0/8"
              />
            </Field>
            <Field>
              <Label>Target Domains</Label>
              <Input
                value={td.domains || ''}
                onChange={handleChange('target_details', 'domains')}
                placeholder="example.com, subdomain.example.com"
              />
            </Field>
            <Field>
              <Label>Target Email Addresses</Label>
              <Input
                value={td.email_addresses || ''}
                onChange={handleChange('target_details', 'email_addresses')}
                placeholder="admin@example.com, user@example.com"
              />
            </Field>
          </FieldGrid>
        );

      case 'cloud-pentest':
        return (
          <FieldGrid>
            <Field>
              <Label>
                <Cloud />
                Cloud Provider *
              </Label>
              <Input
                value={td.cloud_provider || ''}
                onChange={handleChange('target_details', 'cloud_provider')}
                placeholder="AWS, Azure, GCP, etc."
                required
              />
            </Field>
            <Field>
              <Label>Account ID</Label>
              <Input
                value={td.account_id || ''}
                onChange={handleChange('target_details', 'account_id')}
                placeholder="123456789012"
              />
            </Field>
          </FieldGrid>
        );

      default:
        return (
          <ErrorMessage>
            <AlertCircle />
            <h3>Unknown scan type: {scanType}</h3>
            <p>The requested scan type is not supported.</p>
          </ErrorMessage>
        );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitting(true);

    // Map the scan type to backend expected value
    const backendScanType = scanTypeMapping[scanType];
    if (!backendScanType) {
      toast.error('Invalid scan type selected');
      setSubmitting(false);
      return;
    }

    // Prepare the target_details with arrays where required
    const td = form.target_details;

    const payloadTargetDetails = {
      ...td,

      // URLs array for web-app and thin-client (ensure array)
      urls:
        backendScanType === 'web' || backendScanType === 'thin-client'
          ? Array.isArray(td.urls)
            ? td.urls
            : td.urls
            ? [td.urls]
            : []
          : undefined,

      // For network and firewall, ips as array
      ips:
        backendScanType === 'network' || backendScanType === 'firewall'
          ? td.ips
            ? toArray(td.ips)
            : []
          : undefined,

      // Thick client application_files as array
      application_files:
        backendScanType === 'thick-client'
          ? td.application_files
            ? toArray(td.application_files)
            : []
          : undefined,

      // Red team fields as arrays
      ip_ranges:
        backendScanType === 'red-team'
          ? td.ip_ranges
            ? toArray(td.ip_ranges)
            : []
          : undefined,
      domains:
        backendScanType === 'red-team'
          ? td.domains
            ? toArray(td.domains)
            : []
          : undefined,
      email_addresses:
        backendScanType === 'red-team'
          ? td.email_addresses
            ? toArray(td.email_addresses)
            : []
          : undefined,
    };

    // Remove undefined keys (optional cleanup)
    Object.keys(payloadTargetDetails).forEach(
      key => payloadTargetDetails[key] === undefined && delete payloadTargetDetails[key]
    );

    // Construct the payload with mapped scan type and normalized target_details
    const payload = {
      scan_type: backendScanType,
      target_details: payloadTargetDetails,
      objectives: form.objectives,
      scope: form.scope,
      timeline: form.timeline,
      proposed_start_date: form.proposed_start_date,
      proposed_end_date: form.proposed_end_date,
      timezone: form.timezone,
      has_exclusions: form.has_exclusions,
      exclusions: form.exclusions,
      contact_info: form.contact_info
    };

    try {
      const res = await fetch('https://api.zerodayops.com/api/advanced/pre-engagement-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        if (res.status === 401) {
          toast.error('Session expired. Please log in again.');
          navigate('/login');
          return;
        }
        throw new Error(errorData.message || 'Failed to submit form');
      }

      const data = await res.json();
      toast.success(data.message || 'Pre-engagement form submitted successfully!');
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <PageContainer>
        <ContentWrapper>
          <FormCard>
            <ErrorMessage>
              <AlertCircle />
              <h2>Invalid Scan Type</h2>
              <p>The requested scan type is not supported.</p>
            </ErrorMessage>
          </FormCard>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2937',
            color: '#f9fafb',
            border: '1px solid #374151',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500'
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#f9fafb',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f9fafb',
            },
          },
        }}
      />

      <ContentWrapper>
        {/* Header */}
        {/* <Header>
          <IconContainer gradient={config.gradient}>
            <IconComponent />
          </IconContainer>
          <MainTitle>Pre-Engagement Form</MainTitle>
          <SubTitle>{config.title}</SubTitle>
          <Description>
            Please provide the following information to initiate your security assessment.
            All fields marked with an asterisk (*) are required.
          </Description>
        </Header> */}

        {/* Form Card */}
        <FormCard>
          <Form onSubmit={handleSubmit}>
            {/* Target Details Section */}
            <Section>
              <SectionHeader>
                <SectionIcon gradient={config.gradient}>
                  <IconComponent />
                </SectionIcon>
                <SectionTitle>Target Details</SectionTitle>
              </SectionHeader>
              {renderTargetFields()}
            </Section>

            {/* Assessment Details Section */}
            <Section>
              <SectionHeader>
                <SectionIcon gradient="linear-gradient(135deg, #10b981, #047857)">
                  <FileText />
                </SectionIcon>
                <SectionTitle>Assessment Details</SectionTitle>
              </SectionHeader>

              <FieldGrid>
                <Field fullWidth>
                  <Label>Assessment Objectives *</Label>
                  <TextArea
                    rows={4}
                    value={form.objectives}
                    onChange={handleChange('objectives')}
                    placeholder="Describe the main objectives and goals for this security assessment..."
                    required
                  />
                </Field>

                <Field fullWidth>
                  <Label>Scope of Testing *</Label>
                  <TextArea
                    rows={3}
                    value={form.scope}
                    onChange={handleChange('scope')}
                    placeholder="Define what is included and excluded from the testing scope..."
                    required
                  />
                </Field>

                {/* <Field>
                  <Label>
                    <Calendar />
                    Preferred Timeline
                  </Label>
                  <Input
                    value={form.timeline}
                    onChange={handleChange('timeline')}
                    placeholder="e.g., 2 weeks, By end of month, ASAP"
                  />
                </Field> */}
              </FieldGrid>
            </Section>

            {/* Services Timing Preferences Section */}
            <Section>
              <SectionHeader>
                <SectionIcon gradient="linear-gradient(135deg, #6366f1, #4f46e5)">
                  <Clock />
                </SectionIcon>
                <SectionTitle>Services Timing Preferences / Restrictions</SectionTitle>
              </SectionHeader>
              {/* <Description style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
                Proposed schedule to conduct the service
              </Description> */}

              <FieldGrid>
                <Field>
                  <Label>
                    Proposed audit start date & time *
                  </Label>
                  <Input
                    type="datetime-local"
                    value={form.proposed_start_date}
                    onChange={handleChange('proposed_start_date')}
                    required
                  />
                  <HelpText>dd-MM-yyyy HH:MM AM/PM</HelpText>
                </Field>

                <Field>
                  <Label>
                    Proposed audit end date & time *
                  </Label>
                  <Input
                    type="datetime-local"
                    value={form.proposed_end_date}
                    onChange={handleChange('proposed_end_date')}
                    required
                  />
                  <HelpText>dd-MM-yyyy HH:MM AM/PM</HelpText>
                </Field>

                <Field fullWidth>
                  <Label>
                    <MapPin />
                    Timezone *
                  </Label>
                  <Select
                    value={form.timezone}
                    onChange={handleChange('timezone')}
                    required
                  >
                    {timezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </Select>
                  <HelpText>Select the timezone</HelpText>
                </Field>
              </FieldGrid>

              {/* Exclusions */}
              <ExclusionsSection>
                <ExclusionsTitle>Exclusions</ExclusionsTitle>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="exclusions"
                      checked={form.has_exclusions}
                      onChange={() => setForm(prev => ({ ...prev, has_exclusions: true }))}
                    />
                    <span>Yes</span>
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="exclusions"
                      checked={!form.has_exclusions}
                      onChange={() => setForm(prev => ({ ...prev, has_exclusions: false, exclusions: '' }))}
                    />
                    <span>No</span>
                  </RadioLabel>
                </RadioGroup>
                
                {form.has_exclusions && (
                  <Field>
                    <TextArea
                      rows={4}
                      value={form.exclusions}
                      onChange={handleChange('exclusions')}
                      placeholder="Please insert any specific assets, services or time periods that should be excluded from the scope with their justification."
                    />
                    <HelpText>
                      Please insert any specific assets, services or time periods that should be excluded from the scope with their justification.
                    </HelpText>
                  </Field>
                )}
              </ExclusionsSection>
            </Section>

            {/* Contact Information Section */}
            <Section>
              <SectionHeader>
                <SectionIcon gradient="linear-gradient(135deg, #8b5cf6, #6d28d9)">
                  <User />
                </SectionIcon>
                <SectionTitle>Contact Information</SectionTitle>
              </SectionHeader>

              <FieldGrid>
                <Field>
                  <Label>
                    <User />
                    Contact Name *
                  </Label>
                  <Input
                    value={form.contact_info.name}
                    onChange={handleChange('contact_info', 'name')}
                    placeholder="Your full name"
                    required
                  />
                </Field>

                <Field>
                  <Label>
                    <Mail />
                    Contact Email *
                  </Label>
                  <Input
                    type="email"
                    value={form.contact_info.email}
                    onChange={handleChange('contact_info', 'email')}
                    placeholder="your.email@company.com"
                    required
                  />
                </Field>

                <Field>
                  <Label>
                    <Phone />
                    Phone Number *
                  </Label>
                  <Input
                    type="tel"
                    value={form.contact_info.phone}
                    onChange={handleChange('contact_info', 'phone')}
                    placeholder="+1 (555) 123-4567"
                    required
                  />
                </Field>

                <Field>
                  <Label>
                    <Clock />
                    Preferred Contact Time
                  </Label>
                  <Input
                    value={form.contact_info.preferred_contact_time}
                    onChange={handleChange('contact_info', 'preferred_contact_time')}
                    placeholder="e.g., 9 AM - 5 PM EST, Weekdays only"
                  />
                </Field>
              </FieldGrid>
            </Section>

            {/* Submit Section */}
            <SubmitSection>
              <SubmitButton
                type="submit"
                disabled={submitting}
                gradient={config.gradient}
              >
                {submitting ? (
                  <>
                    <Loader2 style={{ animation: 'spin 1s linear infinite' }} />
                    Submitting Form...
                  </>
                ) : (
                  <>
                    <CheckCircle />
                    Submit Pre-Engagement Form
                  </>
                )}
              </SubmitButton>
            </SubmitSection>
          </Form>
        </FormCard>
      </ContentWrapper>
    </PageContainer>
  );
}